import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import {
  companyListQueryOptions,
  departmentListQueryOptions,
  designationListQueryOptions,
} from '@/features/organization/api/organization.api';
import { userTypesQueryOptions } from '@/features/users/api/userTypes.api';
import { parseNumberFilter, toFilterOptions } from '@/utils/filters';
import { ACTIVITY_STATUS, ACTIVITY_STATUS_LABELS } from '../constants/moTravel.constants';

const ACTIVITY_STATUS_OPTIONS: FilterOption[] = ACTIVITY_STATUS.map((status) => ({
  label: ACTIVITY_STATUS_LABELS[status],
  value: status,
}));

interface UseMoTravelFilterFieldsParams {
  isFilterPanelOpen: boolean;
  companyId: string;
}

export function useMoTravelFilterFields({
  isFilterPanelOpen,
  companyId,
}: UseMoTravelFilterFieldsParams) {
  const selectedCompanyId = parseNumberFilter(companyId);
  const enabled = isFilterPanelOpen;

  const userTypes = useQuery({ ...userTypesQueryOptions(), enabled });
  const companies = useQuery({ ...companyListQueryOptions(), enabled });
  const designations = useQuery({ ...designationListQueryOptions(), enabled });
  const departments = useQuery({
    ...departmentListQueryOptions(
      selectedCompanyId === undefined ? {} : { companyId: selectedCompanyId },
    ),
    enabled,
  });

  const fields = useMemo<FilterField[]>(
    () => [
      {
        key: 'activityStatus',
        label: 'Status',
        type: 'select',
        options: ACTIVITY_STATUS_OPTIONS,
      },
      {
        key: 'userTypeId',
        label: 'User Type',
        type: 'select',
        options: toFilterOptions(userTypes.data),
      },
      {
        key: 'companyId',
        label: 'Company',
        type: 'select',
        options: toFilterOptions(companies.data),
        searchable: true,
      },
      {
        key: 'departmentId',
        label: 'Department',
        type: 'select',
        options: toFilterOptions(departments.data),
        searchable: true,
      },
      {
        key: 'designationId',
        label: 'Designation',
        type: 'select',
        options: toFilterOptions(designations.data),
        searchable: true,
      },
      { key: 'hrId', label: 'HR ID', type: 'text', placeholder: 'Enter HR ID' },
      { key: 'username', label: 'User Name', type: 'text', placeholder: 'Enter user name' },
      { key: 'mobile', label: 'Mobile', type: 'text', placeholder: 'Enter mobile number' },
    ],
    [userTypes.data, companies.data, departments.data, designations.data],
  );

  const hasOptionsError =
    userTypes.isError || companies.isError || departments.isError || designations.isError;

  return { fields, hasOptionsError };
}
